import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, updateDoc, collection, onSnapshot } from 'firebase/firestore';
import toast from 'react-hot-toast';

export default function LiveMonitor({ allClasses }) {
    const [students, setStudents] = useState([]);
    const [batches, setBatches] = useState([]);
    const [injectTarget, setInjectTarget] = useState(null); 
    const [injectType, setInjectType] = useState('student');
    const [selectedEmail, setSelectedEmail] = useState('');
    const [selectedBatch, setSelectedBatch] = useState('');
    const [confirmEndClassId, setConfirmEndClassId] = useState(null);

    useEffect(() => {
        const unsub1 = onSnapshot(collection(db, "users"), snap => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setStudents(list.filter(u => u.role === 'STUDENT'));
        });
        const unsub2 = onSnapshot(collection(db, "batches"), snap => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setBatches(list);
        });
        return () => { unsub1(); unsub2(); };
    }, []);

    const liveClasses = allClasses.filter(c => c.status === 'LIVE');

    const handleInject = async (classId) => {
        const targetClass = allClasses.find(c => c.id === classId);
        if (!targetClass) return;

        let emailsToAdd = [];
        if (injectType === 'student') {
            if (!selectedEmail) return toast.error("Select a student first!");
            emailsToAdd = [selectedEmail];
        } else {
            if (!selectedBatch) return toast.error("Select a batch first!");
            const b = batches.find(x => x.id === selectedBatch);
            if (!b || !b.students || b.students.length === 0) return toast.error("This batch has no students!");
            emailsToAdd = b.students.map(s => s.email);
        }

        const currentStudents = targetClass.students || [];
        const newStudents = [...new Set([...currentStudents, ...emailsToAdd])];

        const loading = toast.loading("Injecting into Live Class...");
        try {
            await updateDoc(doc(db, "classes", classId), { students: newStudents });
            toast.success("Successfully injected! 🚀", { id: loading });
            setInjectTarget(null); 
            setSelectedEmail(''); 
            setSelectedBatch('');
        } catch (err) {
            toast.error("Failed to inject.", { id: loading });
        }
    };

    const handleForceEndClass = async (classId) => {
        const loading = toast.loading("Ending Live Class...");
        try {
            await updateDoc(doc(db, "classes", classId), {
                status: 'COMPLETED',
                endedAt: Date.now()
            });
            toast.success("Class ended successfully! 🛑", { id: loading });
        } catch (err) {
            toast.error("Failed to end class.", { id: loading });
        }
    };

    if (liveClasses.length === 0) {
        return (
            <div style={{textAlign: 'center', padding: '50px', background: 'var(--bg-primary)', borderRadius: '12px' }}>
                <div style={{fontSize: '50px', marginBottom: '15px' }}>😴</div>
                <h2 style={{color: 'var(--text-primary)', margin: '0 0 10px 0' }}>No Live Classes Right Now</h2>
                <p style={{color: 'var(--text-secondary)' }}>When a coach starts a class, it will appear here for you to monitor and inject students.</p>
            </div>
        );
    }

    return (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
            {liveClasses.map(c => (
                <div key={c.id} style={{background: 'var(--bg-primary)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', boxShadow: 'var(--panel-shadow)' }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                        <div>
                            <div style={{color: '#ef4444', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                                <span style={{animation: 'pulse 1.5s infinite' }}>🔴</span> LIVE NOW
                            </div>
                            <h3 style={{margin: '0 0 5px 0', color: 'var(--text-primary)' }}>{c.topic}</h3>
                            
                            {/* 🔥 DISPLAYING BATCH NAME HERE FOR OWNER */}
                            {c.batchTitle && (
                                <div style={{color: '#f59e0b', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>🏫 Batch: {c.batchTitle}</div>
                            )}

                            <div style={{color: 'var(--text-secondary)', fontSize: '13px' }}>👨‍🏫 Coach: {c.teacherId}</div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '13px' }}>👥 Students inside: {c.students?.length || 0}</div>
                        </div>
                        <button 
                            onClick={() => setConfirmEndClassId(c.id)}
                            style={{
                                padding: '8px 12px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444',
                                border: '1px solid #ef4444',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                flexShrink: 0
                            }}
                            title="Force end this class"
                        >
                            🛑 End Class
                        </button>
                    </div>

                    {injectTarget === c.id ? (
                        <div style={{background: 'var(--bg-primary)', padding: '15px', borderRadius: '8px', border: '1px solid #3b82f6', marginTop: '15px' }}>
                            <div style={{display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                <button onClick={() => setInjectType('student')} style={{flex: 1, padding: '8px', background: injectType === 'student' ? '#3b82f6' : 'transparent', color: injectType === 'student' ? 'white' : 'var(--text-secondary)', border: '1px solid #3b82f6', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>👤 Single Student</button>
                                <button onClick={() => setInjectType('batch')} style={{flex: 1, padding: '8px', background: injectType === 'batch' ? '#3b82f6' : 'transparent', color: injectType === 'batch' ? 'white' : 'var(--text-secondary)', border: '1px solid #3b82f6', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>🏫 Entire Batch</button>
                            </div>

                            {injectType === 'student' ? (
                                <select value={selectedEmail} onChange={e=>setSelectedEmail(e.target.value)} style={{width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', marginBottom: '10px' }}>
                                    <option value="">-- Select Student to Inject --</option>
                                    {students.map(s => <option key={s.email} value={s.email}>{s.name} ({s.email})</option>)}
                                </select>
                            ) : (
                                <select value={selectedBatch} onChange={e=>setSelectedBatch(e.target.value)} style={{width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', marginBottom: '10px' }}>
                                    <option value="">-- Select Batch to Inject --</option>
                                    {batches.map(b => <option key={b.id} value={b.id}>{b.title} ({b.students?.length || 0} students)</option>)}
                                </select>
                            )}

                            <div style={{display: 'flex', gap: '10px' }}>
                                <button onClick={() => setInjectTarget(null)} style={{flex: 1, padding: '10px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={() => handleInject(c.id)} style={{flex: 1, padding: '10px', background: '#10b981', color: 'black', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>🚀 Drop In!</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setInjectTarget(c.id)} style={{width: '100%', padding: '12px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '15px' }}>
                            ➕ Inject Student / Batch
                        </button>
                    )}
                </div>
            ))}

            {/* Custom Confirm Modal */}
            {confirmEndClassId && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 10000,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        background: 'var(--bg-primary)',
                        padding: '30px',
                        borderRadius: '16px',
                        maxWidth: '400px',
                        textAlign: 'center',
                        boxShadow: 'var(--neu-shadow)',
                        border: '1px solid rgba(163, 177, 198, 0.4)'
                    }}>
                        <div style={{fontSize: '40px', marginBottom: '10px'}}>⚠️</div>
                        <h3 style={{color: 'var(--text-primary)', margin: '0 0 10px 0'}}>Force End Class?</h3>
                        <p style={{color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '25px'}}>
                            Are you sure you want to FORCE END this live class? This will disconnect all students and the coach immediately.
                        </p>
                        <div style={{display: 'flex', gap: '15px'}}>
                            <button 
                                onClick={() => setConfirmEndClassId(null)}
                                style={{flex: 1, padding: '12px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderRadius: '10px', border: '1px solid rgba(163, 177, 198, 0.3)', cursor: 'pointer', fontWeight: 'bold'}}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => { handleForceEndClass(confirmEndClassId); setConfirmEndClassId(null); }}
                                style={{flex: 1, padding: '12px', background: '#ef4444', color: 'white', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 'bold'}}
                            >
                                Force End
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
